import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  documents as docsApi,
  sources as sourcesApi,
  userTokens as userTokensApi,
  type DocumentKind,
  type DocumentVisibility,
} from "@/lib/api";
import { buildStarterPrompt } from "@/lib/starterPrompt";

const MCP_BASE = "https://coauthor.skarp.app";
const USER_WIDE_URL = `${MCP_BASE}/mcp`;
const ONBOARDED_FLAG = "coauthor.onboarded.v1";

/**
 * First-run onboarding wizard. Three steps that frame the product correctly:
 *
 *   1. "What are you working on?" — title + (optional) kickoff brief.
 *   2. "Add an agent" — mint a connector token, show paste-into helpers.
 *   3. Done — land on the new document with a hint.
 *
 * Once completed (or skipped), `localStorage` records the flag so we don't
 * show this on every empty-dashboard load. Phase 11 can promote this to a
 * `users.has_completed_onboarding` column in PG.
 */
interface CreatedDocSummary {
  id: string;
  title: string;
  kind: DocumentKind;
  language: string | null;
}

export function Onboarding() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [createdDoc, setCreatedDoc] = useState<CreatedDocSummary | null>(null);
  const docId = createdDoc?.id ?? null;

  const finish = (id: string | null) => {
    try {
      localStorage.setItem(ONBOARDED_FLAG, "1");
    } catch {
      /* no-op */
    }
    void qc.invalidateQueries({ queryKey: ["documents"] });
    navigate(id ? `/d/${id}` : "/", { replace: true });
  };

  const skip = () => finish(docId);

  return (
    <div className="min-h-screen flex items-start justify-center px-4 py-8 sm:py-16 bg-zinc-50 dark:bg-zinc-950">
      <div className="w-full max-w-2xl">
        <header className="mb-8 flex items-baseline justify-between">
          <div>
            <h1 className="text-3xl font-semibold">Welcome to Coauthor</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Two-minute setup so an AI agent can start drafting on your behalf.
            </p>
          </div>
          <button
            type="button"
            onClick={skip}
            className="text-xs text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
          >
            Skip
          </button>
        </header>

        <Stepper current={step} />

        {step === 1 && (
          <StepOne
            onCreated={(doc) => {
              setCreatedDoc(doc);
              setStep(2);
            }}
          />
        )}
        {step === 2 && docId && (
          <StepTwo
            docId={docId}
            onContinue={() => setStep(3)}
            onSkip={() => finish(docId)}
          />
        )}
        {step === 3 && createdDoc && (
          <StepThree doc={createdDoc} onDone={() => finish(createdDoc.id)} />
        )}
      </div>
    </div>
  );
}

function Stepper({ current }: { current: 1 | 2 | 3 }) {
  const labels = ["Document", "Connect agent", "Done"];
  return (
    <ol className="flex items-center gap-2 mb-8 text-xs">
      {labels.map((label, i) => {
        const n = (i + 1) as 1 | 2 | 3;
        const active = n === current;
        const done = n < current;
        return (
          <li key={label} className="flex items-center gap-2">
            <span
              className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-medium ${
                done
                  ? "bg-emerald-500 text-white"
                  : active
                    ? "bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white"
                    : "bg-zinc-200 dark:bg-zinc-800 text-zinc-500"
              }`}
            >
              {done ? "✓" : n}
            </span>
            <span
              className={
                active
                  ? "text-zinc-900 dark:text-zinc-100 font-medium"
                  : "text-zinc-500"
              }
            >
              {label}
            </span>
            {n < 3 && <span className="w-8 h-px bg-zinc-200 dark:bg-zinc-800 mx-2" />}
          </li>
        );
      })}
    </ol>
  );
}

// ---- Step 1: title + optional brief ----------------------------------------

function StepOne({
  onCreated,
}: {
  onCreated: (doc: CreatedDocSummary) => void;
}) {
  const [title, setTitle] = useState("");
  const [briefText, setBriefText] = useState("");
  const [visibility, setVisibility] = useState<DocumentVisibility>("public");
  const [kind, setKind] = useState<DocumentKind>("general");
  const [language, setLanguage] = useState("");
  const briefFileRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Phase 12.7 UX revision: type / visibility / brief collapsed behind an
  // expander. Most users want general+public+no-brief; the rest opt in.
  // Auto-expand if the user has actively chosen anything non-default so
  // their selections stay visible when the form re-renders.
  const isDefaultShape =
    kind === "general"
    && visibility === "public"
    && !language
    && !briefText
    && !briefFileRef.current?.files?.length;
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const createDoc = useMutation({
    mutationFn: async (input: {
      title: string;
      visibility: DocumentVisibility;
      kind: DocumentKind;
      language?: string;
    }) => {
      const created = await docsApi.create(input);
      return created.document;
    },
  });

  const uploadBrief = useMutation({
    mutationFn: async (vars: {
      docId: string;
      kind: "brief";
      title: string;
      file?: File;
      text?: string;
    }) => {
      if (vars.file) {
        return sourcesApi.upload(vars.docId, vars.file, {
          kind: vars.kind,
          title: vars.title,
        });
      }
      // Synthesize a markdown File from the textarea so we can reuse the
      // multipart endpoint without a JSON-only fork.
      const blob = new Blob([vars.text ?? ""], { type: "text/markdown" });
      const synthetic = new File([blob], `${vars.title || "brief"}.md`, {
        type: "text/markdown",
      });
      return sourcesApi.upload(vars.docId, synthetic, {
        kind: vars.kind,
        title: vars.title,
      });
    },
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = title.trim();
    if (!trimmed) {
      setError("A title is required.");
      return;
    }
    try {
      const doc = await createDoc.mutateAsync({
        title: trimmed,
        visibility,
        kind,
        language: language.trim() || undefined,
      });
      const file = briefFileRef.current?.files?.[0];
      const text = briefText.trim();
      if (file || text) {
        try {
          await uploadBrief.mutateAsync({
            docId: doc.id,
            kind: "brief",
            title: file?.name?.replace(/\.[^.]+$/, "") || `${trimmed} — kickoff brief`,
            file: file ?? undefined,
            text: file ? undefined : text,
          });
        } catch (e) {
          // Don't block onboarding on a brief upload failure — surface the
          // error and continue. The user can re-upload from the Sources page.
          if (e instanceof ApiError) {
            const body = e.body as { error?: string } | null;
            setError(`Brief upload failed: ${body?.error ?? e.status}`);
          } else {
            setError(`Brief upload failed: ${String(e)}`);
          }
        }
      }
      onCreated({
        id: doc.id,
        title: doc.title,
        kind: doc.kind,
        language: doc.language,
      });
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body as { error?: string } | null;
        setError(body?.error ?? `Failed to create document (${e.status})`);
      } else {
        setError(String(e));
      }
    }
  };

  const busy = createDoc.isPending || uploadBrief.isPending;

  return (
    <form
      onSubmit={submit}
      className="rounded-lg bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-6 space-y-5"
    >
      <div>
        <h2 className="text-lg font-medium mb-1">What are you working on?</h2>
        <p className="text-sm text-zinc-500">
          Give the document a title so your agents know how to refer to it.
        </p>
      </div>

      <label className="block">
        <span className="text-sm font-medium">Document title</span>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
          maxLength={200}
          placeholder={
            kind === "recipe"
              ? "How to memoize async fetches in Python"
              : kind === "qa"
                ? "What's the right way to handle X?"
                : "A document I want my agent to draft"
          }
          className="mt-1 block w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm"
        />
        <span className="block mt-2 text-xs text-zinc-500">
          {advancedOpen || !isDefaultShape ? (
            <>You're configuring this manually below.</>
          ) : (
            <>
              We'll create it as a <span className="font-medium">general public</span> document.{" "}
              <button
                type="button"
                onClick={() => setAdvancedOpen(true)}
                className="underline hover:text-zinc-900 dark:hover:text-zinc-100"
              >
                Change type, visibility, or add a brief
              </button>
            </>
          )}
        </span>
      </label>

      {/* Phase 12.7 UX revision: collapse type/visibility/brief behind one
          expander so first-time users only see one input above the fold.
          The summary above the disclosure lets people see the defaults
          without opening it; ~80% of new docs will skip this entirely. */}
      <details
        open={advancedOpen || !isDefaultShape}
        onToggle={(e) => setAdvancedOpen((e.currentTarget as HTMLDetailsElement).open)}
        className="rounded-md border border-zinc-200 dark:border-zinc-800"
      >
        <summary className="cursor-pointer px-4 py-2.5 text-sm font-medium select-none">
          Advanced options
          <span className="ml-2 text-xs font-normal text-zinc-500">
            {kindLabel(kind)} · {visibility === "public" ? "Public" : "Private"}
            {language ? ` · ${language}` : ""}
            {briefText ? " · brief attached" : ""}
          </span>
        </summary>
        <div className="px-4 pb-4 pt-1 space-y-4 border-t border-zinc-200 dark:border-zinc-800">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium">Type</span>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as DocumentKind)}
                className="mt-1 block w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm"
              >
                <option value="general">General document</option>
                <option value="recipe">Code recipe</option>
                <option value="qa">Q&amp;A answer</option>
                <option value="reference">Reference / cheatsheet</option>
                <option value="style_guide">Style guide</option>
              </select>
              <span className="block mt-1 text-xs text-zinc-500">
                {kind === "recipe"
                  ? "Auto-scaffolds: Use case · Code · Explanation · Alternatives & edge cases."
                  : kind === "qa"
                    ? "Canonical answer to a single question, written collaboratively."
                    : kind === "reference"
                      ? "Cheatsheet, algorithm explanation, or library walkthrough."
                      : kind === "style_guide"
                        ? "Living document for team or project conventions."
                        : "Open-ended document. Agents will outline as they see fit."}
              </span>
            </label>
            <label className={`block ${kind === "recipe" || kind === "reference" ? "" : "sm:opacity-50"}`}>
              <span className="text-sm font-medium">Language (optional)</span>
              <input
                type="text"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                maxLength={32}
                placeholder="python, typescript, rust…"
                className="mt-1 block w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm font-mono"
              />
              <span className="block mt-1 text-xs text-zinc-500">
                Used to filter Discover. Most useful for recipes.
              </span>
            </label>
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Visibility</legend>
            <label className="flex items-start gap-3 rounded-md border border-zinc-200 dark:border-zinc-800 p-3 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-900">
              <input
                type="radio"
                name="visibility"
                value="public"
                checked={visibility === "public"}
                onChange={() => setVisibility("public")}
                className="mt-1"
              />
              <span className="text-sm">
                <span className="font-medium">Public</span>
                <span className="block text-xs text-zinc-500 mt-0.5">
                  Listed in the discovery feed. Anyone — humans or other people's
                  agents — can read and propose contributions. You still gate
                  approval and publication.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 rounded-md border border-zinc-200 dark:border-zinc-800 p-3 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-900">
              <input
                type="radio"
                name="visibility"
                value="private"
                checked={visibility === "private"}
                onChange={() => setVisibility("private")}
                className="mt-1"
              />
              <span className="text-sm">
                <span className="font-medium">Private</span>
                <span className="block text-xs text-zinc-500 mt-0.5">
                  Only invited members can see this. Use for closed-group
                  collaboration.
                </span>
              </span>
            </label>
          </fieldset>

          <div className="rounded-md border border-zinc-200 dark:border-zinc-800 p-4">
            <div className="text-sm font-medium mb-1">Kickoff brief (optional)</div>
            <p className="text-xs text-zinc-500 mb-3">
              A few sentences telling the agents what this document is for, who it's
              for, and what good looks like. You can paste markdown or upload a
              file. Add more sources later.
            </p>
            <textarea
              rows={5}
              value={briefText}
              onChange={(e) => setBriefText(e.target.value)}
              placeholder="Audience, goal, key constraints, tone…"
              className="block w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm"
            />
            <div className="mt-3 flex items-center gap-3">
              <span className="text-xs text-zinc-500">or upload</span>
              <input
                ref={briefFileRef}
                type="file"
                accept=".md,.markdown,.txt,.pdf,text/markdown,text/plain,application/pdf"
                className="text-xs flex-1"
              />
            </div>
          </div>
        </div>
      </details>

      {error && (
        <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy || !title.trim()}
          className="rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          {busy ? "Creating…" : "Create document"}
        </button>
      </div>
    </form>
  );
}

// ---- Step 2: mint connector token + paste-into helpers ----------------------

function StepTwo({
  onContinue,
  onSkip,
}: {
  docId: string;
  onContinue: () => void;
  onSkip: () => void;
}) {
  const [labelDraft, setLabelDraft] = useState("My agent");
  const [minted, setMinted] = useState<{
    token: string;
    label: string | null;
  } | null>(null);
  const [hasExisting, setHasExisting] = useState<boolean | null>(null);

  // Check whether the user already has any user-wide tokens — if so, they
  // can skip minting another and reuse the existing one.
  useEffect(() => {
    let cancelled = false;
    void userTokensApi
      .list()
      .then((r) => {
        if (!cancelled) {
          setHasExisting((r.tokens ?? []).some((t) => !t.revoked_at));
        }
      })
      .catch(() => {
        if (!cancelled) setHasExisting(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const mintMut = useMutation({
    mutationFn: (label: string) => userTokensApi.mint(label || undefined),
    onSuccess: (res) => setMinted({ token: res.token, label: res.label }),
  });

  return (
    <div className="rounded-lg bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-6 space-y-5">
      <div>
        <h2 className="text-lg font-medium mb-1">Connect your AI agent</h2>
        <p className="text-sm text-zinc-500">
          Generate one bearer token — your agent's identity. Paste it once into
          Claude.ai / ChatGPT / Codex and it works across every document you
          create or contribute to. Revoke any time.
        </p>
      </div>

      {hasExisting && !minted && (
        <div className="rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm">
          <span className="font-medium text-amber-900 dark:text-amber-200">
            You already have an agent token.
          </span>{" "}
          <span className="text-amber-800 dark:text-amber-300">
            If it's already pasted into Claude.ai / Codex, you can skip this
            step. To remint, click Generate below — the old one keeps working
            until you revoke it.
          </span>
        </div>
      )}

      {!minted && (
        <div className="rounded-md border border-zinc-200 dark:border-zinc-800 p-4">
          <div className="text-sm font-medium mb-2">Token label</div>
          <div className="flex gap-2 items-start">
            <input
              type="text"
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              maxLength={80}
              placeholder="My agent (Claude.ai on laptop)"
              className="flex-1 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-1.5 text-sm"
            />
            <button
              type="button"
              onClick={() => mintMut.mutate(labelDraft.trim())}
              disabled={mintMut.isPending}
              className="text-sm rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-3 py-1.5 disabled:opacity-50"
            >
              {mintMut.isPending ? "Minting…" : "Generate token"}
            </button>
          </div>
          {mintMut.error && (
            <div className="text-xs text-red-600 dark:text-red-400 mt-2">
              {mintMut.error instanceof ApiError
                ? ((mintMut.error.body as { error?: string } | null)?.error ??
                    `${mintMut.error.status}`)
                : String(mintMut.error)}
            </div>
          )}
          <p className="text-xs text-zinc-500 mt-2">
            Shown once. Copy it now — it cannot be retrieved later.
          </p>
        </div>
      )}

      {minted && (
        <div className="rounded-md border-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 p-4 space-y-4">
          <div className="text-sm font-medium text-emerald-900 dark:text-emerald-100">
            Token minted{minted.label ? ` — ${minted.label}` : ""}. Copy it now.
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-1">
              MCP URL
            </div>
            <CopyField value={USER_WIDE_URL} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-1">
              Bearer token
            </div>
            <CopyField value={minted.token} mono />
          </div>
          <div className="grid sm:grid-cols-3 gap-3 text-xs pt-1">
            <PasteCard
              title="Claude.ai"
              steps={[
                "Settings → Connectors → Add custom connector",
                "Paste the URL and click Add",
                "Paste the bearer token in the OAuth dialog",
              ]}
            />
            <PasteCard
              title="ChatGPT"
              steps={[
                "Settings → Connectors → Add",
                "Paste the URL above",
                "Choose Bearer auth, paste the token",
              ]}
            />
            <PasteCard
              title="Codex (Mac)"
              steps={[
                "Settings → MCP Servers → Add",
                "Paste URL into URL field",
                "Add header Authorization: Bearer <token>",
              ]}
            />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onSkip}
          className="text-xs text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
        >
          {hasExisting ? "I already have a token" : "I'll add an agent later"}
        </button>
        <button
          type="button"
          onClick={onContinue}
          disabled={!minted && !hasExisting}
          className="rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

// ---- Step 3: confirmation --------------------------------------------------

function StepThree({
  doc,
  onDone,
}: {
  doc: CreatedDocSummary;
  onDone: () => void;
}) {
  const prompt = buildStarterPrompt({
    sessionId: doc.id,
    title: doc.title,
    kind: doc.kind,
    language: doc.language,
  });
  const [copied, setCopied] = useState(false);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard might be unavailable */
    }
  };

  const copyAndOpen = (url: string) => async () => {
    await copyPrompt();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="rounded-lg bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-6 space-y-5">
      <div className="text-center">
        <div className="text-4xl">🎉</div>
        <h2 className="text-lg font-medium mt-2">You're set up.</h2>
        <p className="text-sm text-zinc-500 max-w-md mx-auto mt-1">
          One more step. Paste this prompt into Claude.ai (or ChatGPT / Codex)
          to get your agent moving on the doc.
        </p>
      </div>

      <div className="rounded-md border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 p-3">
        <pre className="text-xs leading-relaxed whitespace-pre-wrap font-mono text-zinc-800 dark:text-zinc-200 max-h-72 overflow-y-auto">
          {prompt}
        </pre>
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        <button
          type="button"
          onClick={copyPrompt}
          className="text-sm rounded-md bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 px-4 py-2 font-medium hover:opacity-90"
        >
          {copied ? "Copied!" : "Copy prompt"}
        </button>
        <button
          type="button"
          onClick={copyAndOpen("https://claude.ai/new")}
          className="text-sm rounded-md border border-zinc-300 dark:border-zinc-700 px-4 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-800"
        >
          Copy + open Claude.ai
        </button>
        <button
          type="button"
          onClick={copyAndOpen("https://chatgpt.com/")}
          className="text-sm rounded-md border border-zinc-300 dark:border-zinc-700 px-4 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-800"
        >
          Copy + open ChatGPT
        </button>
      </div>

      <div className="text-center pt-3 border-t border-zinc-200 dark:border-zinc-800">
        <button
          type="button"
          onClick={onDone}
          className="text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 underline"
        >
          Open the document
        </button>
        <p className="text-xs text-zinc-400 mt-2">
          You can copy the prompt again from the document page.
        </p>
        <p className="text-xs text-zinc-400 mt-1">
          Free to start.{" "}
          <Link to="/billing" className="underline hover:text-zinc-700 dark:hover:text-zinc-200">
            Upgrade in Billing
          </Link>{" "}
          when you need more.
        </p>
      </div>
    </div>
  );
}

// ---- Local helpers ---------------------------------------------------------

/** Human-friendly label for a DocumentKind. Used in the Advanced summary. */
function kindLabel(kind: DocumentKind): string {
  switch (kind) {
    case "recipe":
      return "Recipe";
    case "qa":
      return "Q&A";
    case "reference":
      return "Reference";
    case "style_guide":
      return "Style guide";
    case "general":
    default:
      return "General";
  }
}

function CopyField({ value, mono = false }: { value: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  };
  return (
    <div className="flex gap-2 items-stretch">
      <input
        readOnly
        value={value}
        onFocus={(e) => e.currentTarget.select()}
        className={`flex-1 rounded-md border border-emerald-200 dark:border-emerald-900 bg-white dark:bg-zinc-950 px-3 py-1.5 text-xs ${mono ? "font-mono" : ""}`}
      />
      <button
        type="button"
        onClick={onCopy}
        className="text-xs rounded-md border border-emerald-300 dark:border-emerald-800 px-3 py-1.5 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 flex-shrink-0"
      >
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}

function PasteCard({ title, steps }: { title: string; steps: string[] }) {
  return (
    <div className="rounded-md bg-white dark:bg-zinc-950 border border-emerald-200 dark:border-emerald-900 p-3">
      <div className="text-sm font-medium mb-1.5">{title}</div>
      <ol className="text-xs text-zinc-600 dark:text-zinc-400 space-y-1 list-decimal list-inside">
        {steps.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ol>
    </div>
  );
}

export function shouldShowOnboarding(): boolean {
  try {
    return localStorage.getItem(ONBOARDED_FLAG) !== "1";
  } catch {
    return true;
  }
}
